import {RuntimeAdapter} from '@assistant-ui/core/react';
import {Suggestions} from '@assistant-ui/core/store';
import {AuiProvider, useAui} from '@assistant-ui/store';
import {useWbAgentClient} from '@wandb/weave/lib/WbAgent/useWbAgentClient';
import React, {useCallback, useMemo} from 'react';

import {WBAgentRuntime} from '../../agentChat/runtime/backend';
import {
  AgentChatStoreProvider,
  selectActiveThread,
  useAgentChatStore,
} from '../../agentChat/store';
import {useAgentChatRuntime} from '../../agentChat/useAgentChatRuntime';
import {useNavContext} from '../NavContextProvider';
import {SUGGESTION_ITEMS} from '../WBAgent/WBAgentSuggestionItem';
import {
  AgentChatAvailabilityContext,
  ShareTracesPolicyContext,
  useComputeAgentChatAvailability,
  useComputeShareTracesPolicy,
} from './useAgentChatAvailability';

/**
 * Top-level provider for the Agent Chat feature.
 *
 * When the feature is available (gate + org settings pass), mounts the chat
 * store and AUI runtime.
 */
export function AgentChatProvider({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const availability = useComputeAgentChatAvailability();
  const shareTracesPolicy = useComputeShareTracesPolicy();

  return (
    <AgentChatAvailabilityContext.Provider value={availability}>
      <ShareTracesPolicyContext.Provider value={shareTracesPolicy}>
        <AgentChatStoreProvider>
          {availability.isAvailable ? (
            <AssistantRuntime>{children}</AssistantRuntime>
          ) : (
            children
          )}
        </AgentChatStoreProvider>
      </ShareTracesPolicyContext.Provider>
    </AgentChatAvailabilityContext.Provider>
  );
}

function AssistantRuntime({children}: {children: React.ReactNode}) {
  const {params} = useNavContext();
  const wbAgentClient = useWbAgentClient(params.entityName, params.projectName);
  const store = useAgentChatStore();

  const getParentNodeId = useCallback(
    (nodeId: string) => {
      const thread = selectActiveThread(store.getState());
      return thread?.messageTree.parentId[nodeId] ?? null;
    },
    [store]
  );

  const agentRuntime = useMemo(
    () =>
      wbAgentClient ? new WBAgentRuntime(wbAgentClient, getParentNodeId) : null,
    [wbAgentClient, getParentNodeId]
  );

  const auiRuntime = useAgentChatRuntime(agentRuntime, params);
  const aui = useAui({
    threads: RuntimeAdapter(auiRuntime),
    suggestions: Suggestions(SUGGESTION_ITEMS),
  });

  return <AuiProvider value={aui}>{children}</AuiProvider>;
}
